interface Settings {
  url: string;
  method: "POST" | "GET";
  body?: string | FormData;
  headers?: HeadersInit;
}

interface Props {
  settings: Settings;
  headers?: HeadersInit;
  withErrorToast?: boolean;
}

export const handleFetchError = async (
  error: any,
  withErrorToast?: boolean
): Promise<GenericApiResponseJSON> => {
  const { res } = error;
  const genericError: Record<string, any> = {};

  genericError.res = res;
  genericError.data = { success: false, message: "An error occured." };
  genericError.originalError = error;

  try {
    if (typeof res !== "undefined" && typeof res.data === "object" && res.data.message) {
      genericError.data = res.data;
    }
  } catch (err) {
    console.error(err);
  }

  if (withErrorToast && genericError?.data?.message) {
    console.error(genericError.data.message);
  }

  throw genericError;
};

/**
 * Inject "__RequestVerificationToken" header needed by Kentico against
 * Cross-Site Request Forgery.
 */
const getCSRFConfig = (config: RequestInit): RequestInit => {
  const token = (window.WIDGETS_CONFIG?.general as General)?.requestVerificationToken ?? "";

  if (!token) {
    console.error('Missing "requestVerificationToken" in general config!');
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      __RequestVerificationToken: token,
    },
  };
};

async function fetchJSON<T extends GenericApiResponseJSON>({
  settings,
  headers = { Accept: "application/json", "Content-Type": "application/json" },
  withErrorToast,
}: Props): Promise<T | GenericApiResponseJSON> {
  const config: RequestInit = {
    method: settings.method,
    body: settings.body || null,
    credentials: "same-origin",
  };

  if (headers) {
    config.headers = headers;
  }

  const { url } = settings;
  const sanitizedUrl = url.startsWith("/") ? url : `/${url}`;

  // inject headers - they cannot be set as defaults on line 62
  const configWithVerificationToken = getCSRFConfig(config);

  return fetch(sanitizedUrl, configWithVerificationToken)
    .then((response) => response.json())
    .then((response: T) => {
      if (response && response.success) {
        return response;
      }

      // eslint-disable-next-line no-throw-literal
      throw {
        res: {
          data: {
            message: response.message,
            payload: response.payload,
            success: response.success,
          },
        },
      };
    })
    .catch((error) => handleFetchError(error, withErrorToast));
}

export default fetchJSON;
